import { Add16 } from '@carbon/icons-react';
import { Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AttachmentsAPI,
  FileAttachment,
  FileUploadStatus,
  getUpdatedFileList,
  UploadFileWithAttachment,
} from 'api/attachments';
import { Task } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import {
  AddItemButton,
  AddItemWrapper,
  // AddTask,
  SectionName,
  SectionWrapper,
  TitleWrapper,
} from './components';
import { CpAttachmentGridView } from './cp-task-attachments-section';

interface Props {
  task: Task;
  refetchTask?: () => void;
}

export const useCpTaskAttachmentUtils = (
  task: Task,
  refetchTask?: () => void,
) => {
  const [filesList, setFilesList] = useState<UploadFileWithAttachment[]>([]);
  const latestFileList = useRef<UploadFileWithAttachment[]>(filesList);
  const translate = useTranslate();

  const handleOnChange = useCallback((args) => {
    const { file } = args;

    setFilesList([...latestFileList.current, file]);
  }, []);

  const onUploadAttachment = useCallback(
    (file: File) =>
      AttachmentsAPI.attachTaskFiles(file, {
        taskId: task?.taskId,
        projectId: task?.project?.projectId,
      }),
    [task?.project?.projectId, task?.taskId],
  );

  const updateFile = useCallback(
    ({
      attachment,
      file,
      status,
    }: {
      attachment?: FileAttachment;
      file: RcFile;
      status: FileUploadStatus;
    }) => {
      const latestFiles = latestFileList.current;
      const fileToUpdate = latestFiles.find((f) => f.uid === file.uid);

      if (!fileToUpdate) {
        return;
      }

      const result = getUpdatedFileList(
        fileToUpdate,
        latestFiles,
        attachment,
        status,
      );

      if (result) {
        setFilesList(result);
      }
    },
    [],
  );

  const customUploadRequest = useCallback(
    (args) => {
      const { file } = args;

      onUploadAttachment(file)
        .then((res) => {
          updateFile({
            attachment: res,
            file,
            status: FileUploadStatus.Done,
          });
          setFilesList([]);
          refetchTask?.();
        })
        .catch(({ response = {} }) => {
          const errorData = response.data || {};
          const [error = {}] = errorData.errors || [];

          if (error.code === 'ATTACHMENT_SIZE_EXCEEDED') {
            toastService({
              title: translate('attachment.error.size.title'),
              message: translate('attachment.add.description'),
            }).error();
          }
          updateFile({
            attachment: undefined,
            file,
            status: FileUploadStatus.Error,
          });
        });
    },
    [onUploadAttachment, refetchTask, translate, updateFile],
  );

  useEffect(() => {
    latestFileList.current = filesList;
  }, [filesList]);

  return useMemo(
    () => ({
      filesList,
      handleOnChange,
      customUploadRequest,
    }),
    [customUploadRequest, filesList, handleOnChange],
  );
};

export const CpTaskMoreDetailsSection: React.FC<Props> = ({
  task,
  refetchTask,
}) => {
  const { customUploadRequest, filesList, handleOnChange } =
    useCpTaskAttachmentUtils(task, refetchTask);

  const showAttachments = useMemo(
    () => !task?.attachments || task?.attachments.length === 0,
    [task?.attachments],
  );

  // const showSubTasks = useMemo(
  //   () => !task?.subTasks || task?.subTasks.length === 0,
  //   [task?.subTasks],
  // );

  // const showDependentTasks = useMemo(
  //   () => !task?.dependentTasks || task?.dependentTasks.length === 0,
  //   [task?.dependentTasks],
  // );

  if (!showAttachments) {
    return <></>;
  }

  return (
    <SectionWrapper>
      <TitleWrapper>
        <SectionName>
          <FormattedMessage id="more_actions" />
        </SectionName>
      </TitleWrapper>
      {filesList?.length > 0 ? (
        <CpAttachmentGridView
          attachmentFiles={filesList}
          onDownloadFile={() => {}}
          onRemoveFile={() => {}}
        />
      ) : (
        <></>
      )}
      {showAttachments ? (
        <Upload
          onChange={handleOnChange}
          customRequest={customUploadRequest}
          multiple
          showUploadList={false}
        >
          <AddItemWrapper>
            <Add16 />
            <AddItemButton>
              <FormattedMessage id="attachment.add" />
            </AddItemButton>
          </AddItemWrapper>
        </Upload>
      ) : (
        <></>
      )}
      {/* {showSubTasks ? (
        <AddItemWrapper withTopMargin>
          <Add16 />
          <AddItemButton>
            <AddTask>
              <FormattedMessage id="sub_tasks.add" />
            </AddTask>
          </AddItemButton>
        </AddItemWrapper>
      ) : (
        <></>
      )} */}
      {/* {showDependentTasks ? (
        <AddItemWrapper withTopMargin>
          <Add16 />
          <AddItemButton>
            <FormattedMessage id="dependent_tasks.add" />
          </AddItemButton>
        </AddItemWrapper>
      ) : (
        <></>
      )} */}
    </SectionWrapper>
  );
};
